import { Box, Typography, Button } from '@mui/material';
import apiClient from '../../services/apiClient';
import { useEffect, useState } from 'react';
import DynamicTable from '../../components/Table/DataTable';
import { IconEdit, IconPlus, IconRefresh, IconSearch, IconTrash } from '../../components/icons/icons';
import TablePaginationActions from '../../components/Table/tablePagination';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import ChargeCodeDialog from './VetChargeCodeForms';
import { ChargeCodeStatusModal } from './ChargeCodeStatusModal';

const ChargeCodeComponent = () => {
  // Pagination state
  const [page, setPage] = useState(0); // zero-based index for table
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [searchText, setSearchText] = useState('');
  // Data & UI states
  const [chargeCodes, setChargeCodes] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<Record<number, string>>({});

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedChargeCode, setSelectedChargeCode] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chargeCodeToDelete, setChargeCodeToDelete] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState<number | ''>('');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [currentChargeCode, setCurrentChargeCode] = useState<any | null>(null);
  const [appliedFilters, setAppliedFilters] = useState({
    provider: '',
    serviceType: '',
    searchText: '',
    status: '',
  }) as any;

  const fetchProviders = async () => {
    try {
      const response: any = await apiClient(`v1/resource/Provider/all`, { method: 'GET' });
      const providerMap: Record<number, string> = {};
      response.data.providers.forEach((provider: any) => {
        const user = provider.user;
        if (user) providerMap[provider.id] = `${user.first_name} ${user.last_name}`;
      });
      setProviders(providerMap);
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchChargeCodes = async (filters: any) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.append('page', (page + 1).toString());
    params.append('limit', rowsPerPage.toString());
    if (filters.provider) params.append('provider_id', filters.provider);
    if (filters.serviceType) params.append('service_type', filters.serviceType);
    if (filters.searchText) params.append('search', filters.searchText);
    if (filters.status !== '') {
      params.append('is_active', filters.status);
    }

    try {
      const response: any = await apiClient(`v1/resource/ChargeCode?${params.toString()}`, { method: 'GET' });
      setChargeCodes(response.data.charge_codes || []);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    setPage(0);
    fetchChargeCodes(appliedFilters);
  }, [appliedFilters, page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    setAppliedFilters({
      provider: selectedProvider,
      serviceType: selectedServiceType,
      searchText: searchText,
      status: selectedStatus,
    });
  };

  const handleClearFilters = () => {
    setSelectedProvider('');
    setSelectedServiceType('');
    setSearchText('');
    setSelectedStatus('');
    setPage(0);
    setAppliedFilters({ provider: '', serviceType: '', searchText: '', status: '' });
  };
  const openStatusModal = (row: any) => {
    setCurrentChargeCode(row);
    setStatusModalOpen(true);
  };
  const columns = [
    {
      key: 'serial',
      label: 'S.No',
      align: 'center',
      render: (_row: any, index: number) => page * rowsPerPage + index + 1,
    },
    { key: 'veterinarian', label: 'Veterinarian', align: 'center', render: (row: any) => providers[row.provider_id] || 'N/A', },
    { key: 'service_type', label: 'Service Type', align: 'center', render: (row: any) => row?.service_type },
    { key: 'charge_code', label: 'Charge Code', align: 'center', render: (row: any) => row?.charge_code },
    { key: 'service_name', label: 'Service Name', align: 'center', render: (row: any) => row?.service_name ?? '-' },
    {
      key: 'amount',
      label: 'Amount',
      align: 'center',
      render: (row: any) => {
        if (row?.amount == null) return <Typography fontWeight="bold">N/A</Typography>;

        const amount = parseFloat(row.amount);
        const displayAmount = Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);

        return (
          <Typography
            fontWeight="bold"
            sx={{
              background:
                amount > 0
                  ? 'linear-gradient(90deg, #4caf50, #81c784)'
                  : 'linear-gradient(90deg, #f44336, #e57373)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            ₹{displayAmount}
          </Typography>
        );
      },
    },

    {
      key: 'tax',
      label: 'Tax',
      align: 'Left',
      render: (row: any) => {
        const baseAmount = parseFloat(row?.amount || '0');
        let taxDetails: { name: string; value: number; type: string }[] = [];
        let totalTax = 0;

        try {
          let taxCodes: any[] = [];

          // If tax_codes is a string, parse it
          if (typeof row.tax_codes === 'string') {
            taxCodes = JSON.parse(row.tax_codes);
          } else if (Array.isArray(row.tax_codes)) {
            taxCodes = row.tax_codes;
          }

          // Ensure each tax code is an object
          taxCodes = taxCodes.map((t: any) => (typeof t === 'string' ? JSON.parse(t) : t));

          taxCodes.forEach((tax: any) => {
            const rate = parseFloat(tax.rate || '0');
            let value = 0;
            if (tax.type === 'percentage') {
              value = (baseAmount * rate) / 100;
            } else if (tax.type === 'adhoc') {
              value = rate;
            }
            totalTax += value;
            taxDetails.push({ name: tax.name || tax.code, value, type: tax.type });
          });
        } catch (err) {
          console.error('Error parsing tax_codes:', err);
        }

        const totalAmount = totalTax;

        return (
          <Box>
            {taxDetails.length > 0 ? (
              <>
                {taxDetails.map((tax, index) => (
                  <Typography variant="body2" key={index}>
                    {tax.name} ({tax.type}): ₹{tax.value.toFixed(2)}
                  </Typography>
                ))}
                <Typography variant="body2" fontWeight="bold" mt={0.5}>
                  Total Amount: ₹{totalAmount.toFixed(2)}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No Tax
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      key: 'total_amount',
      label: 'Total Amount',
      align: 'center',
      render: (row: any) => {
        console.log('row', row)
        const totalAmount = parseFloat(row?.total_amount || '0');

        const displayAmount = Number.isInteger(totalAmount)
          ? totalAmount.toString()
          : totalAmount.toFixed(2);

        return (
          <Typography fontWeight="bold" color="primary">
            ₹{displayAmount}
          </Typography>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      align: "left",
      render: (row: any) => {
        let bgColor = "";
        let text = "";

        switch (row?.is_active) {
          case 1:
            text = "Active";
            bgColor = "bg-green-100 text-green-800"; // Tailwind example
            break;
          case 0:
            text = "Inactive";
            bgColor = "bg-yellow-100 text-yellow-800";
            break;
          case 2:
            text = "Deactivated";
            bgColor = "bg-red-100 text-red-800";
            break;
          default:
            text = "-";
            bgColor = "bg-gray-100 text-gray-800";
        }

        return (
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${bgColor}`}>
            {text}
          </span>
        );
      },
    },

  ];

  const confirmDelete = (id: string | number) => {
    const chargeCode = chargeCodes.find((c) => c.id === id);
    setChargeCodeToDelete(chargeCode || null);
    setDeleteDialogOpen(true);
  };

  const deleteChargeCodeApi = async (id: string | number) => {
    await apiClient(`v1/resource/ChargeCode/${id}`, { method: 'DELETE' });
  };

  const handleDeleteSuccess = () => {
    fetchChargeCodes(appliedFilters);
    setChargeCodeToDelete(null);
    setDeleteDialogOpen(false);
  };

  const actions = [
    {
      label: 'Edit',
      color: '#4caf50',
      icon: <IconEdit size={20} />,
      onClick: (row: any) => {
        setSelectedChargeCode(row);
        setDialogOpen(true);
      },
    },
    {
      label: 'Change Status',
      color: '#ff9800',
      icon: <IconRefresh size={20} />,
      onClick: openStatusModal,
    },
    {
      label: 'Delete',
      color: '#f44336',
      icon: <IconTrash size={20} />,
      onClick: (row: any) => confirmDelete(row.id),
    },
  ];

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleAddChargeCode = () => {
    setSelectedChargeCode(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedChargeCode(null);
  };

  const handleFormSuccess = () => {
    fetchChargeCodes(appliedFilters);
    setDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setChargeCodeToDelete(null);
  };

  return (
    <Box>
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-md">
        <div className="flex flex-1 flex-wrap gap-3 md:gap-4 items-center">
          {/* Provider Filter */}
          <select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            className="border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
          >
            <option value="">All Providers</option>
            {Object.entries(providers).map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>

          {/* Service Type Filter */}
          <select
            value={selectedServiceType}
            onChange={(e) => setSelectedServiceType(e.target.value)}
            className="border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
          >
            <option value="">All Service Types</option>
            <option value="Tele Consult">Tele Consult</option>
            <option value="Walk In">Walk In</option>
            <option value="Home visit">Home visit</option>
          </select>

          {/* Search Input */}
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search charge codes..."
            className="flex-1 border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition min-w-[200px]"
          />

          <select
            value={selectedStatus}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedStatus(val === '' ? '' : Number(val)); // convert to number, keep empty string for "All"
            }}
            className="border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
          >
            <option value="">All Statuses</option>
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
            <option value={2}>Deactivated</option>
          </select>


          <button
            onClick={handleSearch}
            className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 transition"
          >
            <IconSearch size={18} /> Search
          </button>
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-2 px-5 py-2 bg-gray-300 text-gray-800 rounded-lg shadow hover:bg-gray-400 transition"
          >
            Clear Filters
          </button>
        </div>

        {/* Add Charge Code */}
        <button
          onClick={() => { setSelectedChargeCode(null); setDialogOpen(true); }}
          className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition mt-3 md:mt-0"
        >
          <IconPlus size={18} /> Add Charge Code
        </button>
      </div>



      <DynamicTable
        columns={columns}
        data={chargeCodes}
        loading={loading}
        emptyMessage={"No charge codes found"}
        actions={actions}
        page={page}
        error={error}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        ActionsComponent={TablePaginationActions}
      />

      <ChargeCodeDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleFormSuccess}
        initialData={selectedChargeCode}
      />
      <ChargeCodeStatusModal
        open={statusModalOpen}
        currentChargeCode={currentChargeCode}
         type="vet"
        onClose={() => setStatusModalOpen(false)}
        onUpdate={() => fetchChargeCodes(page)}
      />
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        itemId={chargeCodeToDelete?.id}
        itemName={chargeCodeToDelete?.charge_code}
        deleteApi={deleteChargeCodeApi}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </Box>
  );
};

export default ChargeCodeComponent;
